import { useMemo, useState } from "react";
import { array, object, string, InferType } from "yup";
import { useField, useFormikContext } from "formik";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Code,
  Flex,
  IconButton,
  Select,
  Separator,
  Tabs,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Bell, FileText, Info, Layers, Maximize2, Minimize2, Plus, RotateCcw, Save, Users, X } from "lucide-react";
import toast from "react-hot-toast";
import SelectField from "@/components/form/SelectField";
import ImagePickerField from "@/components/ui/ImagePickerField";
import PushButtonsField, { PushButtonRow } from "@/components/ui/PushButtonsField";
import IosButtonGroupPicker from "@/components/ui/IosButtonGroupPicker";
import VariableInputField from "./shared/VariableInputField";
import VariableMenu from "./shared/VariableMenu";
import FieldSectionHeader from "./shared/FieldSectionHeader";
import {
  PushTemplate,
  SaveTemplateInput,
  tenantPushService,
} from "@/services/breeze/TenantPushService";
import { WorkflowStep } from "@/types/breeze/Workflow";
import PushTemplateEditorDialog from "@/components/push/PushTemplateEditorDialog";
import { messageFromError } from "@/utils/text";

export const sendPushNotificationValidationSchema = object({
  parameters: object().shape({
    template_type: string(),
    title: string().when("template_type", {
      is: (v?: string) => !v,
      then: (s) => s.required("Title is required (or pick a template)"),
    }),
    body: string().when("template_type", {
      is: (v?: string) => !v,
      then: (s) => s.required("Body is required (or pick a template)"),
    }),
    image_url: string(),
    click_action: string(),
    category: string(),
    buttons: array().of(
      object({
        id: string(),
        label: string(),
        click_action: string(),
      }),
    ),
    audience: object().shape({
      kind: string()
        .oneOf(["user", "users", "entity", "all"])
        .required("Audience kind is required"),
      user_id: string(),
      user_ids: array().of(string()),
      owner_entity_name: string(),
      owner_entity_id: string(),
      provider: string(),
    }),
  }),
});

export type SendPushNotificationFormValues = InferType<
  typeof sendPushNotificationValidationSchema
>;

interface Props {
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}

type TabKey = "content" | "mappings";
type Mode = "template" | "custom";

export default function SendPushNotificationFields({ existingSteps = [], triggerEntity }: Props) {
  const { values, setFieldValue } = useFormikContext<{
    parameters?: {
      audience?: { kind?: string };
      title?: string;
      body?: string;
      image_url?: string;
      click_action?: string;
      buttons?: PushButtonRow[];
      category?: string;
      template_type?: string;
    };
  }>();
  const imageUrl = values?.parameters?.image_url ?? "";
  const buttons: PushButtonRow[] = values?.parameters?.buttons ?? [];
  const category = values?.parameters?.category ?? "";
  const templateType = values?.parameters?.template_type ?? "";
  const titleInline = values?.parameters?.title ?? "";
  const bodyInline = values?.parameters?.body ?? "";
  const clickActionInline = values?.parameters?.click_action ?? "";

  const { data: templatesData } = useQuery({
    queryKey: ["push-templates"],
    queryFn: () => tenantPushService.getTemplates(),
  });
  const templates = templatesData?.templates ?? [];
  const selectedTemplate = templates.find((t) => t.template_type === templateType);

  const { data: categoriesData } = useQuery({
    queryKey: ["push-categories"],
    queryFn: () => tenantPushService.getCategories(),
  });
  const selectedCategory = (categoriesData?.categories ?? []).find((c) => c.category === category);
  const lockedByGroup = selectedCategory && (selectedCategory.ios_action_order?.length ?? 0) > 0
    ? (selectedCategory.label || selectedCategory.category)
    : null;

  const [mode, setMode] = useState<Mode>(templateType ? "template" : "custom");

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    if (next === "custom") {
      setFieldValue("parameters.template_type", "");
    }
    setMode(next);
  };

  const effective = useMemo(() => {
    if (mode === "template" && selectedTemplate) {
      return {
        title: titleInline || selectedTemplate.title || "",
        body: bodyInline || selectedTemplate.body || "",
        click_action: clickActionInline || selectedTemplate.click_action || "",
        buttons: buttons.length > 0
          ? buttons
          : (selectedTemplate.buttons ?? []).map((b) => ({
              id: b.id,
              label: b.label,
              click_action: b.click_action ?? "",
            })),
      };
    }
    return {
      title: titleInline,
      body: bodyInline,
      click_action: clickActionInline,
      buttons,
    };
  }, [mode, selectedTemplate, titleInline, bodyInline, clickActionInline, buttons]);

  const [samples, setSamples] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<TabKey>("content");

  const detectedTokens = useMemo(
    () => detectTokens(effective),
    [effective],
  );

  return (
    <Flex direction="column" gap="4">
      <AudienceTab existingSteps={existingSteps} triggerEntity={triggerEntity} />

      <Tabs.Root value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <Tabs.List>
          <Tabs.Trigger value="content"><Bell size={14} /> Content</Tabs.Trigger>
          <Tabs.Trigger value="mappings">
            <Layers size={14} /> Mappings
            {detectedTokens.length > 0 && (
              <Badge color="gray" variant="soft" ml="1">{detectedTokens.length}</Badge>
            )}
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="content">
          <Box mt="4">
            <ContentTab
              mode={mode}
              switchMode={switchMode}
              templates={templates}
              selectedTemplate={selectedTemplate}
              templateType={templateType}
              existingSteps={existingSteps}
              triggerEntity={triggerEntity}
              buttons={buttons}
              category={category}
              imageUrl={imageUrl}
              lockedByGroup={lockedByGroup}
              setFieldValue={setFieldValue}
              titleInline={titleInline}
              bodyInline={bodyInline}
              clickActionInline={clickActionInline}
            />
          </Box>
        </Tabs.Content>

        <Tabs.Content value="mappings">
          <Box mt="4">
            <TokenMappings effective={effective} samples={samples} setSamples={setSamples} />
          </Box>
        </Tabs.Content>
      </Tabs.Root>
    </Flex>
  );
}

function ContentTab({
  mode,
  switchMode,
  templates,
  selectedTemplate,
  templateType,
  existingSteps,
  triggerEntity,
  buttons,
  category,
  imageUrl,
  lockedByGroup,
  setFieldValue,
  titleInline,
  bodyInline,
  clickActionInline,
}: {
  mode: Mode;
  switchMode: (next: Mode) => void;
  templates: PushTemplate[];
  selectedTemplate?: PushTemplate;
  templateType: string;
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
  buttons: PushButtonRow[];
  category: string;
  imageUrl: string;
  lockedByGroup: string | null;
  setFieldValue: (name: string, value: unknown) => void;
  titleInline: string;
  bodyInline: string;
  clickActionInline: string;
}) {
  return (
    <Flex direction="column" gap="4">
      <ModeToggle mode={mode} onChange={switchMode} />

      {mode === "template" ? (
        <TemplatePickerPanel
          templates={templates}
          selectedTemplate={selectedTemplate}
          templateType={templateType}
          existingSteps={existingSteps}
          triggerEntity={triggerEntity}
          buttons={buttons}
          category={category}
          imageUrl={imageUrl}
          lockedByGroup={lockedByGroup}
          setFieldValue={setFieldValue}
          titleInline={titleInline}
          bodyInline={bodyInline}
          clickActionInline={clickActionInline}
        />
      ) : (
        <CustomContentPanel
          existingSteps={existingSteps}
          triggerEntity={triggerEntity}
          buttons={buttons}
          category={category}
          imageUrl={imageUrl}
          lockedByGroup={lockedByGroup}
          setFieldValue={setFieldValue}
        />
      )}
    </Flex>
  );
}

function ModeToggle({ mode, onChange }: { mode: Mode; onChange: (next: Mode) => void }) {
  const opts: Array<{ key: Mode; label: string; sub: string }> = [
    { key: "template", label: "Use a template", sub: "Reuse a saved push template by id" },
    { key: "custom", label: "Custom content", sub: "Write the title, body and tap target here" },
  ];
  return (
    <Flex gap="2" wrap="wrap">
      {opts.map((o) => {
        const active = mode === o.key;
        return (
          <Card
            key={o.key}
            onClick={() => onChange(o.key)}
            style={{
              flex: "1 1 220px",
              cursor: "pointer",
              borderColor: active ? "var(--accent-9)" : "var(--gray-a5)",
              borderWidth: 1,
              borderStyle: "solid",
              background: active ? "var(--accent-a2)" : undefined,
              contain: "none",
              padding: 12,
            }}
          >
            <Text size="2" weight="medium" style={{ display: "block" }}>{o.label}</Text>
            <Text size="1" color="gray" style={{ display: "block" }}>{o.sub}</Text>
          </Card>
        );
      })}
    </Flex>
  );
}

function TemplatePickerPanel({
  templates,
  selectedTemplate,
  templateType,
  existingSteps,
  triggerEntity,
  buttons,
  category,
  imageUrl,
  lockedByGroup,
  setFieldValue,
  titleInline,
  bodyInline,
  clickActionInline,
}: {
  templates: PushTemplate[];
  selectedTemplate?: PushTemplate;
  templateType: string;
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
  buttons: PushButtonRow[];
  category: string;
  imageUrl: string;
  lockedByGroup: string | null;
  setFieldValue: (name: string, value: unknown) => void;
  titleInline: string;
  bodyInline: string;
  clickActionInline: string;
}) {
  const queryClient = useQueryClient();
  const tplCategory = (selectedTemplate?.category ?? "") || category;
  const [createOpen, setCreateOpen] = useState(false);
  const [popOut, setPopOut] = useState(false);

  const updateTemplate = useMutation({
    mutationFn: (input: SaveTemplateInput) => {
      if (!selectedTemplate) throw new Error("No template selected");
      return tenantPushService.updateTemplate(selectedTemplate.id, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["push-templates"] });
      // Clear the workflow's per-step overrides — they now match the saved
      // template, so storing them again is just noise.
      setFieldValue("parameters.title", "");
      setFieldValue("parameters.body", "");
      setFieldValue("parameters.image_url", "");
      setFieldValue("parameters.click_action", "");
      setFieldValue("parameters.buttons", []);
      setFieldValue("parameters.category", "");
      toast.success("Template updated. All workflows using it will see the new content.");
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not update template")),
  });

  const hasOverrides =
    !!titleInline
    || !!bodyInline
    || !!clickActionInline
    || !!imageUrl
    || !!category
    || buttons.length > 0;

  const saveToTemplate = () => {
    if (!selectedTemplate) return;
    const effectiveButtons = buttons.length > 0
      ? buttons
      : (selectedTemplate.buttons ?? []).map((b) => ({
          id: b.id,
          label: b.label,
          click_action: b.click_action ?? "",
        }));
    updateTemplate.mutate({
      template_type: selectedTemplate.template_type,
      name: selectedTemplate.name ?? undefined,
      description: selectedTemplate.description ?? undefined,
      title: titleInline || selectedTemplate.title,
      body: bodyInline || selectedTemplate.body,
      image_url: imageUrl || selectedTemplate.image_url || undefined,
      click_action: clickActionInline || selectedTemplate.click_action || undefined,
      category: category || selectedTemplate.category || undefined,
      buttons: effectiveButtons.map((b) => ({
        id: b.id,
        label: b.label,
        click_action: b.click_action || undefined,
      })),
      is_active: selectedTemplate.is_active,
    });
  };

  const resetOverrides = () => {
    setFieldValue("parameters.title", "");
    setFieldValue("parameters.body", "");
    setFieldValue("parameters.image_url", "");
    setFieldValue("parameters.click_action", "");
    setFieldValue("parameters.buttons", []);
    setFieldValue("parameters.category", "");
  };

  const body = (
    <Flex direction="column" gap="4">
      <TemplatePickerRow
        templates={templates}
        templateType={templateType}
        setFieldValue={setFieldValue}
        onCreateClick={() => setCreateOpen(true)}
        hasOverrides={selectedTemplate ? hasOverrides : undefined}
        onSave={selectedTemplate ? saveToTemplate : undefined}
        savePending={updateTemplate.isPending}
        popOut={popOut}
        onTogglePopOut={() => setPopOut((v) => !v)}
      />

      {createOpen && (
        <PushTemplateEditorDialog
          initial={null}
          onClose={() => setCreateOpen(false)}
          onSaved={(saved) => {
            setFieldValue("parameters.template_type", saved.template_type);
            setCreateOpen(false);
          }}
        />
      )}

      {templateType && !selectedTemplate && (
        <Callout.Root color="amber">
          <Callout.Icon><Info size={14} /></Callout.Icon>
          <Callout.Text>
            Template <Code size="1" variant="ghost">{templateType}</Code> isn't currently in your tenant's template list — the workflow will fail at run time. Pick from the dropdown or create the template under Push Notifications → Templates.
          </Callout.Text>
        </Callout.Root>
      )}

      {selectedTemplate && (
        <>
          <Callout.Root>
            <Callout.Icon><Info size={14} /></Callout.Icon>
            <Callout.Text>
              Fields below are filled with the template's current content. Edits become <strong>overrides</strong> for this workflow only — the template stays untouched. Click <strong>Save template</strong> above to push your edits back, and every workflow using this template will pick them up.
            </Callout.Text>
          </Callout.Root>

          <FieldSectionHeader
            icon={<Bell size={14} />}
            title="Content"
            subtitle="Pre-filled from the template — edits are overrides until you save them to the template."
          />
          <OverrideField
            name="parameters.title"
            label="Title"
            templateValue={selectedTemplate.title}
            existingSteps={existingSteps}
            triggerEntity={triggerEntity}
          />
          <OverrideField
            name="parameters.body"
            label="Body"
            templateValue={selectedTemplate.body}
            existingSteps={existingSteps}
          />
          <ImagePickerField
            value={imageUrl || selectedTemplate.image_url || ""}
            onChange={(v) => setFieldValue("parameters.image_url", v ?? "")}
            label="Image (optional)"
            description={
              !imageUrl && selectedTemplate.image_url
                ? "Showing the template's image — change to override."
                : undefined
            }
          />

          <Separator size="4" />

          <FieldSectionHeader
            icon={<FileText size={14} />}
            title="On tap"
            subtitle="Where the notification routes when tapped, plus optional buttons."
          />
          <OverrideField
            name="parameters.click_action"
            label="Deep link / URL when tapped"
            templateValue={selectedTemplate.click_action ?? ""}
            description="The app reads this to route the user — works on every platform."
            existingSteps={existingSteps}
            triggerEntity={triggerEntity}
          />

          <IosButtonGroupPicker
            value={tplCategory}
            onChange={(next) => setFieldValue("parameters.category", next)}
            buttons={buttons.length > 0 ? buttons : (selectedTemplate.buttons ?? []).map((b) => ({
              id: b.id,
              label: b.label,
              click_action: b.click_action ?? "",
            }))}
            onAutofillButtons={(next) => setFieldValue("parameters.buttons", next)}
          />
          <PushButtonsField
            value={buttons.length > 0 ? buttons : (selectedTemplate.buttons ?? []).map((b) => ({
              id: b.id,
              label: b.label,
              click_action: b.click_action ?? "",
            }))}
            onChange={(next) => setFieldValue("parameters.buttons", next)}
            lockedByGroup={lockedByGroup}
            hint={
              <>
                The template's buttons render here. Edit a tap target to override for this workflow only, or save changes back to the template above.
              </>
            }
          />

          <Flex justify="end">
            <Button
              type="button"
              variant="soft"
              color="gray"
              size="2"
              disabled={!hasOverrides}
              onClick={resetOverrides}
            >
              <RotateCcw size={14} /> Reset overrides
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  );

  if (popOut) {
    return (
      <Box
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.7)",
          zIndex: 20000,
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          padding: 24,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) setPopOut(false);
        }}
      >
        <Box
          style={{
            background: "var(--color-panel-solid)",
            borderRadius: 8,
            boxShadow: "var(--shadow-6)",
            width: "min(1200px, 100%)",
            height: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Flex justify="end" p="2" style={{ borderBottom: "1px solid var(--gray-a5)" }}>
            <IconButton type="button" variant="ghost" color="gray" onClick={() => setPopOut(false)} title="Close">
              <X size={14} />
            </IconButton>
          </Flex>
          <Box style={{ flex: 1, overflow: "auto", padding: 20 }}>
            {body}
          </Box>
        </Box>
      </Box>
    );
  }

  return body;
}

function CustomContentPanel({
  existingSteps,
  triggerEntity,
  buttons,
  category,
  imageUrl,
  lockedByGroup,
  setFieldValue,
}: {
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
  buttons: PushButtonRow[];
  category: string;
  imageUrl: string;
  lockedByGroup: string | null;
  setFieldValue: (name: string, value: unknown) => void;
}) {
  return (
    <Flex direction="column" gap="4">
      <FieldSectionHeader
        icon={<Bell size={14} />}
        title="Content"
        subtitle="Title and body shown on the lock screen, plus an optional image and tap target."
      />
      <TemplatedField
        name="parameters.title"
        label="Title"
        required
        placeholder="Time to {{ $anythink.trigger.data.activity }}"
        existingSteps={existingSteps}
        triggerEntity={triggerEntity}
      />
      <TemplatedField
        name="parameters.body"
        label="Body"
        required
        placeholder="Your {{ $anythink.trigger.data.session_name }} session is ready."
        existingSteps={existingSteps}
        triggerEntity={triggerEntity}
      />
      <ImagePickerField
        value={imageUrl}
        onChange={(v) => setFieldValue("parameters.image_url", v ?? "")}
        label="Image (optional)"
        description="Android renders natively. iOS needs a Notification Service Extension."
      />

      <Separator size="4" />

      <FieldSectionHeader
        icon={<FileText size={14} />}
        title="On tap"
        subtitle="Where the notification routes when tapped, plus optional action buttons on the banner."
      />
      <TemplatedField
        name="parameters.click_action"
        label="Deep link / URL when the notification is tapped"
        placeholder="/workout/{{ $anythink.trigger.data.workout_id }}"
        description="The app reads this to route the user — works on every platform."
        existingSteps={existingSteps}
        triggerEntity={triggerEntity}
      />

      <IosButtonGroupPicker
        value={category}
        onChange={(next) => setFieldValue("parameters.category", next)}
        buttons={buttons}
        onAutofillButtons={(next) => setFieldValue("parameters.buttons", next)}
      />
      <PushButtonsField
        value={buttons}
        onChange={(next) => setFieldValue("parameters.buttons", next)}
        lockedByGroup={lockedByGroup}
        hint={
          <>
            Optional action buttons rendered on the lock-screen banner. Pick an iOS button group above to lock the id and label across all platforms — only the tap target is then editable per button.
          </>
        }
      />
    </Flex>
  );
}

function AudienceTab({ existingSteps, triggerEntity }: { existingSteps: WorkflowStep[]; triggerEntity?: string }) {
  const { values } = useFormikContext<{ parameters?: { audience?: { kind?: string } } }>();
  const kind = values?.parameters?.audience?.kind ?? "";

  return (
    <Flex direction="column" gap="4">
      <FieldSectionHeader
        icon={<Users size={14} />}
        title="Audience"
        subtitle="Who receives this notification. Most workflow sends are to one user via a variable — list, CRM owner, or broadcast cover the other shapes."
      />

      <SelectField
        name="parameters.audience.kind"
        label="Send to"
        required
        type="string"
      >
        <SelectField.Item value="user">A single user</SelectField.Item>
        <SelectField.Item value="users">A list of users (loop and send per recipient)</SelectField.Item>
        <SelectField.Item value="entity">A CRM record's owner</SelectField.Item>
        <SelectField.Item value="all">Broadcast to every live device</SelectField.Item>
      </SelectField>

      {kind === "user" && (
        <VariableInputField
          name="parameters.audience.user_id"
          label="User id"
          placeholder="{{ $anythink.trigger.data.user_id }}"
          description="A number, or a variable that resolves to one. Leave blank to fall back to the trigger record's user_id."
          existingSteps={existingSteps}
          triggerEntity={triggerEntity}
        />
      )}

      {kind === "users" && (
        <UserIdsListInputField
          name="parameters.audience.user_ids"
          label="User ids (array)"
          placeholder="{{ $anythink.steps.subscribers.data }}"
          description="A variable resolving to an array of user ids — the step loops and sends once per recipient, the same way Create Data's Loop Over Array works."
          existingSteps={existingSteps}
          triggerEntity={triggerEntity}
        />
      )}

      {kind === "entity" && (
        <>
          <VariableInputField
            name="parameters.audience.owner_entity_name"
            label="Owner entity"
            placeholder="contacts"
            description="The CRM entity (e.g. contacts, accounts, leads) whose owner field points at the user to notify."
            required
            existingSteps={existingSteps}
            triggerEntity={triggerEntity}
          />
          <VariableInputField
            name="parameters.audience.owner_entity_id"
            label="Owner record id"
            placeholder="{{ $anythink.trigger.data.id }}"
            description="The record whose owner gets the push. Defaults to the trigger record's id."
            existingSteps={existingSteps}
            triggerEntity={triggerEntity}
          />
        </>
      )}

      {kind === "all" && (
        <Callout.Root color="amber">
          <Callout.Icon><Info size={14} /></Callout.Icon>
          <Callout.Text>
            Every live device for this tenant receives this notification. Useful as a fail-safe rung in an alerting workflow — keep the rest of the workflow gated so it only fires when escalation actually warrants reaching everyone.
          </Callout.Text>
        </Callout.Root>
      )}
    </Flex>
  );
}

function OverrideField({
  name,
  label,
  templateValue,
  description,
  existingSteps,
  triggerEntity,
}: {
  name: string;
  label: string;
  templateValue: string;
  description?: React.ReactNode;
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}) {
  const [field, , helpers] = useField<string>(name);
  const override = field.value ?? "";
  const displayed = override || templateValue;
  const isOverridden = !!override && override !== templateValue;
  const insert = (snippet: string) => {
    const base = override || templateValue;
    const sep = base && !base.endsWith(" ") ? " " : "";
    helpers.setValue(`${base}${sep}${snippet}`);
  };
  return (
    <Box>
      <Flex justify="between" align="center" mb="1">
        <Text as="label" size="2" weight="medium">{label}</Text>
        {isOverridden && (
          <Flex gap="2" align="center">
            <Badge size="1" variant="soft" color="amber">overridden</Badge>
            <Button
              type="button"
              variant="ghost"
              size="1"
              color="gray"
              onClick={() => helpers.setValue("")}
              title="Reset to template value"
            >
              Reset
            </Button>
          </Flex>
        )}
      </Flex>
      {description && (
        <Text size="1" color="gray" mb="1" style={{ display: "block" }}>{description}</Text>
      )}
      <Flex gap="2" align="center">
        <Box style={{ flex: 1 }}>
          <TextField.Root
            value={displayed}
            placeholder={templateValue}
            onChange={(e) => helpers.setValue(e.target.value)}
          />
        </Box>
        <VariableMenu existingSteps={existingSteps ?? []} onInsert={insert} triggerEntity={triggerEntity} />
      </Flex>
    </Box>
  );
}


function TemplatedField({
  name,
  label,
  placeholder,
  description,
  required,
  existingSteps,
  triggerEntity,
}: {
  name: string;
  label: string;
  placeholder?: string;
  description?: React.ReactNode;
  required?: boolean;
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}) {
  const [field, meta, helpers] = useField<string>(name);
  const current = field.value ?? "";
  const insert = (snippet: string) => {
    const sep = current && !current.endsWith(" ") ? " " : "";
    helpers.setValue(`${current}${sep}${snippet}`);
  };
  return (
    <Box>
      <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
        {label} {required && <Text color="red">*</Text>}
      </Text>
      {description && (
        <Text size="1" color="gray" mb="1" style={{ display: "block" }}>{description}</Text>
      )}
      <Flex gap="2" align="center">
        <Box style={{ flex: 1 }}>
          <TextField.Root
            value={current}
            placeholder={placeholder}
            onChange={(e) => helpers.setValue(e.target.value)}
          />
        </Box>
        <VariableMenu existingSteps={existingSteps ?? []} onInsert={insert} triggerEntity={triggerEntity} />
      </Flex>
      {meta.touched && meta.error && (
        <Text size="1" color="red" mt="1" style={{ display: "block" }}>{meta.error}</Text>
      )}
    </Box>
  );
}

function UserIdsListInputField({
  name,
  label,
  placeholder,
  description,
  existingSteps,
  triggerEntity,
}: {
  name: string;
  label: string;
  placeholder?: string;
  description?: React.ReactNode;
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}) {
  const [field, , helpers] = useField<string[] | undefined>(name);
  const first = (field.value ?? [])[0] ?? "";
  const write = (next: string) => helpers.setValue(next ? [next] : []);
  const insert = (snippet: string) => {
    const sep = first && !first.endsWith(" ") ? " " : "";
    write(`${first}${sep}${snippet}`);
  };
  return (
    <Box>
      <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>{label}</Text>
      {description && (
        <Text size="1" color="gray" mb="1" style={{ display: "block" }}>{description}</Text>
      )}
      <Flex gap="2" align="center">
        <Box style={{ flex: 1 }}>
          <TextField.Root
            value={first}
            placeholder={placeholder}
            onChange={(e) => write(e.target.value)}
          />
        </Box>
        <VariableMenu existingSteps={existingSteps ?? []} onInsert={insert} triggerEntity={triggerEntity} />
      </Flex>
    </Box>
  );
}

function detectTokens(effective: {
  title: string;
  body: string;
  click_action: string;
  buttons: PushButtonRow[];
}): Array<{ token: string; locs: string[] }> {
  const usage = new Map<string, Set<string>>();
  const scan = (text: string, where: string) => {
    const re = /\{\{\s*([^}]+?)\s*\}\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const t = m[1].trim();
      if (!usage.has(t)) usage.set(t, new Set());
      usage.get(t)!.add(where);
    }
  };
  scan(effective.title, "Title");
  scan(effective.body, "Body");
  scan(effective.click_action, "On tap");
  effective.buttons.forEach((b, i) => {
    if (b.click_action) scan(b.click_action, `Button ${i + 1} tap target`);
  });
  return Array.from(usage.entries())
    .map(([token, locs]) => ({ token, locs: Array.from(locs) }))
    .sort((a, b) => a.token.localeCompare(b.token));
}

function TokenMappings({
  effective,
  samples,
  setSamples,
}: {
  effective: {
    title: string;
    body: string;
    click_action: string;
    buttons: PushButtonRow[];
  };
  samples: Record<string, string>;
  setSamples: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}) {
  const tokens = useMemo(() => detectTokens(effective), [effective]);
  const resolved = useMemo(() => {
    const sub = (text: string) =>
      text.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (raw, tok) => {
        const sample = samples[tok.trim()];
        return sample && sample.trim() ? sample : raw;
      });
    return {
      title: sub(effective.title),
      body: sub(effective.body),
      click_action: sub(effective.click_action),
    };
  }, [effective, samples]);

  return (
    <Flex direction="column" gap="4">
      <FieldSectionHeader
        icon={<Layers size={14} />}
        title="Mappings & preview"
        subtitle="Every variable used in this notification, plus a live preview rendered with your sample values."
      />

      {tokens.length === 0 ? (
        <Text size="2" color="gray">
          No variables in this notification yet. Use the <Code size="1" variant="ghost">⟡</Code> button next to a field in Content to insert one.
        </Text>
      ) : (
        <Box style={{
          border: "1px solid var(--gray-a5)",
          borderRadius: "var(--radius-3)",
          overflow: "hidden",
        }}>
          {tokens.map(({ token, locs }, i) => (
            <Flex
              key={token}
              align="center"
              gap="3"
              p="2"
              style={{
                borderTop: i > 0 ? "1px solid var(--gray-a4)" : "none",
                background: i % 2 === 0 ? "transparent" : "var(--gray-a2)",
              }}
            >
              <Box style={{ flex: "1 1 40%", minWidth: 0 }}>
                <Code size="1" variant="ghost">{`{{ ${token} }}`}</Code>
                <Text size="1" color="gray" style={{ display: "block" }}>
                  used in {locs.join(", ")}
                </Text>
              </Box>
              <Box style={{ flex: "1 1 60%" }}>
                <TextField.Root
                  size="1"
                  value={samples[token] ?? ""}
                  placeholder="Sample value for the preview"
                  onChange={(e) => setSamples((prev) => ({ ...prev, [token]: e.target.value }))}
                />
              </Box>
            </Flex>
          ))}
        </Box>
      )}

      <Box p="3" style={{
        border: "1px solid var(--accent-a5)",
        borderRadius: "var(--radius-3)",
        background: "var(--accent-a2)",
      }}>
        <Text size="1" color="gray" mb="2" style={{ display: "block" }}>Preview</Text>
        <Text size="3" weight="medium" style={{ display: "block" }}>
          {resolved.title || <Text color="gray">— title —</Text>}
        </Text>
        <Text size="2" style={{ display: "block" }}>
          {resolved.body || <Text color="gray">— body —</Text>}
        </Text>
        {resolved.click_action && (
          <Text size="1" color="gray" mt="2" style={{ display: "block" }}>
            Tap → <Code size="1" variant="ghost">{resolved.click_action}</Code>
          </Text>
        )}
      </Box>
    </Flex>
  );
}

function TemplatePickerRow({
  templates,
  templateType,
  setFieldValue,
  onCreateClick,
  hasOverrides,
  onSave,
  savePending,
  popOut,
  onTogglePopOut,
}: {
  templates: PushTemplate[];
  templateType: string;
  setFieldValue: (name: string, value: unknown) => void;
  onCreateClick: () => void;
  hasOverrides?: boolean;
  onSave?: () => void;
  savePending?: boolean;
  popOut?: boolean;
  onTogglePopOut?: () => void;
}) {
  return (
    <Box>
      <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
        Push template <Text color="red">*</Text>
      </Text>
      <Flex gap="2" align="center" wrap="wrap">
        <Box style={{ flex: 1, minWidth: 220 }}>
          <Select.Root
            value={templateType || ""}
            onValueChange={(v) => setFieldValue("parameters.template_type", v)}
          >
            <Select.Trigger placeholder="Choose a template…" style={{ width: "100%" }} />
            <Select.Content position="popper" style={{ zIndex: 11000 }}>
              {templates.filter((t) => t.is_active).map((t) => (
                <Select.Item key={t.template_type} value={t.template_type}>
                  {t.name ? `${t.name} (${t.template_type})` : t.template_type}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>
        <Button type="button" variant="soft" size="2" onClick={onCreateClick}>
          <Plus size={14} /> New template
        </Button>
        {onSave && (
          <Button
            type="button"
            size="2"
            disabled={!hasOverrides || savePending}
            onClick={onSave}
          >
            <Save size={14} /> {savePending ? "Saving…" : "Save template"}
          </Button>
        )}
        {onTogglePopOut && (
          <IconButton
            type="button"
            variant="soft"
            color="gray"
            size="2"
            onClick={onTogglePopOut}
            title={popOut ? "Pop back inline" : "Pop out"}
          >
            {popOut ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </IconButton>
        )}
      </Flex>
    </Box>
  );
}
